import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitizes user input to prevent XSS attacks
 * @param {string} input - The user input to sanitize
 * @param {Object} options - DOMPurify configuration options
 * @returns {string} Sanitized input safe for display
 */
export const sanitizeInput = (input, options = {}) => {
  if (typeof input !== 'string') {
    return input;
  }

  // Default configuration for text content (no HTML allowed)
  const defaultConfig = {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [], // No attributes allowed
    KEEP_CONTENT: true, // Keep text content even when removing tags
    ...options
  };

  return DOMPurify.sanitize(input, defaultConfig);
};

/**
 * Sanitizes HTML content allowing safe HTML tags
 * Use this when you need to allow some HTML formatting
 * @param {string} html - The HTML content to sanitize
 * @returns {string} Sanitized HTML safe for display
 */
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') {
    return html;
  }

  // Allow only safe HTML tags
  const config = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'code', 'pre'],
    ALLOWED_ATTR: ['href', 'target'],
    ALLOW_DATA_ATTR: false
  };

  return DOMPurify.sanitize(html, config);
};

/**
 * Sanitizes an object's string properties recursively
 * @param {Object} obj - The object to sanitize
 * @returns {Object} Object with sanitized string properties
 */
export const sanitizeObject = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return typeof obj === 'string' ? sanitizeInput(obj) : obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item));
  }

  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Safely store data in localStorage with sanitization
 * @param {string} key - localStorage key
 * @param {any} value - Value to store (will be sanitized if string/object)
 */
export const setLocalStorageSafe = (key, value) => {
  try {
    let sanitizedValue = value;

    if (typeof value === 'string') {
      sanitizedValue = sanitizeInput(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitizedValue = sanitizeObject(value);
    }

    localStorage.setItem(key, typeof sanitizedValue === 'string' ? sanitizedValue : JSON.stringify(sanitizedValue));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

/**
 * Safely retrieve data from localStorage with sanitization
 * @param {string} key - localStorage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any} Sanitized value from localStorage
 */
export const getLocalStorageSafe = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);

    if (item === null) {
      return defaultValue;
    }

    // Try to parse as JSON
    try {
      const parsed = JSON.parse(item);
      return sanitizeObject(parsed);
    } catch {
      // If not JSON, treat as string
      return sanitizeInput(item);
    }
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return defaultValue;
  }
};

export default {
  sanitizeInput,
  sanitizeHTML,
  sanitizeObject,
  setLocalStorageSafe,
  getLocalStorageSafe
};
